import React, { useEffect, useRef, useState } from "react";
import $ from "jquery";
import ConfirmModal from '../modals/ConfirmModal.jsx';
import useMarketPlaceModal from "../../pages/Layout/DashboardLayout/MarketPlaceModal/useMarketPlaceModal";
import { locationWiseData } from '../../services/regionalLevelSales';
import { dummyImage } from "../../../images/index.js";
import { useLocation } from "react-router-dom";
import { getColumnSetting } from "../../services/columnSetting.js";

const NormalTable = ({
  tableId,
  data,
  selectedIds = { categoryId: [], productId: [] },
  onSelectionChange,
  idKey = "id",
  typeKey = "type",
  enableSelection = true,
  prioritizeSelected = false,
  defaultPageLength = 10,
  refreshTrigger,
  onCategoryClick,
  onParentProductClick,
  loading = false,
  sequence,
  filterData = {},
  title,
  range,
  downloadBtnPermission = true,
  onColumnChange = () => { },
  showSelectColumn = false,
  section
}) => {
  const containerRef = useRef(null);
  const selectedIdsRef = useRef(new Set());
  const [delayedEmpty, setDelayedEmpty] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [tempColumns, setTempColumns] = useState([]);
  const [allColumns, setAllColumns] = useState([]);
  const location = useLocation();
  const headersRef = useRef([]);
  const hasWow = data?.some(row => row.wow !== undefined && row.wow !== null);
  const hasMom = data?.some(row => row.mom !== undefined && row.mom !== null);

  const { formattedStart, formattedEnd, level } = filterData;
  useEffect(() => {
    const fetchColumns = async () => {
      try {
        const res = await getColumnSetting(section);

        if (res?.data?.success && Array.isArray(res?.data?.data)) {
          const apiColumns = res.data.data; // full array
          const filteredColumns = apiColumns.filter(
            c => c.key !== "actual_units_display"
          );
          setAllColumns(filteredColumns); // <-- NEW STATE
          setTempColumns(
            filteredColumns.filter(c => c.visible).map(c => c.key)
          );
          setVisibleColumns(
            filteredColumns.filter(c => c.visible).map(c => c.key)
          );
        }
      } catch (err) {
        console.error("Column fetch failed", err);
      }
    };
    if (section) {
      fetchColumns();
    }
  }, [section, tableId, typeKey, location.pathname]);

  useEffect(() => {
    if (headersRef.current.length && tempColumns.length) {
      setVisibleColumns(tempColumns);
    }
  }, [tempColumns]);
  useEffect(() => {
    if (!loading) {
      if (Array.isArray(data) && data.length === 0) {
        setDelayedEmpty(true);
      } else {
        setDelayedEmpty(false);
      }
    } else {
      setDelayedEmpty(false);
    }
  }, [loading, data]);


  useEffect(() => {
    const flatSelected = [
      ...(selectedIds.productId || []),
      ...(selectedIds.categoryId || [])
    ];
    selectedIdsRef.current = new Set(flatSelected);
  }, [selectedIds]);

  const parseCurrency = (value) => {
    if (typeof value !== "string" && typeof value !== "number") return null;

    // Normalize and clean input
    let str = String(value).trim().replace(/[₹,%\s]/g, '').toUpperCase();

    // Handle Crores
    if (str.endsWith('CR')) {
      const num = parseFloat(str.replace('CR', ''));
      return isNaN(num) ? null : num * 10000000;
    }

    // Handle Lakhs
    if (str.endsWith('L')) {
      const num = parseFloat(str.replace('L', ''));
      return isNaN(num) ? null : num * 100000;
    }

    // Handle Thousands
    if (str.endsWith('K')) {
      const num = parseFloat(str.replace('K', ''));
      return isNaN(num) ? null : num * 1000;
    }

    // Handle plain numeric with commas (e.g., "1,962")
    str = str.replace(/,/g, '');
    const numeric = parseFloat(str);
    return isNaN(numeric) ? null : numeric;
  };


  useEffect(() => {
    const initialize = async () => {
      if (!containerRef.current) return;

      try {
        const existingTable = $(`#${tableId}`);
        if ($.fn.DataTable.isDataTable(existingTable)) {
          existingTable.DataTable().clear().destroy(true);
          existingTable.remove();
        }

        if (loading) {
          containerRef.current.innerHTML = getSkeletonTableHTML();
          return;
        }
        if (!loading && (!Array.isArray(data) || data.length === 0)) {
          containerRef.current.innerHTML = getNoDataHTML();
          return;
        }

        if (!Array.isArray(data) || data.length === 0) {
          if (delayedEmpty) containerRef.current.innerHTML = "";
          return;
        }

        await import("../../../datatable.js");

        const sortByType = (a, b) => {
          const priority = { Category: 0, Product: 1 };
          return (priority[a.type] ?? 2) - (priority[b.type] ?? 2);
        };

        const processedData = prioritizeSelected
          ? [...data].sort((a, b) => {
            const idA = a[idKey]?.toString();
            const idB = b[idKey]?.toString();
            const selectedDiff =
              Number(selectedIdsRef.current.has(idB)) -
              Number(selectedIdsRef.current.has(idA));
            if (selectedDiff !== 0) return selectedDiff;
            return sortByType(a, b);
          })
          : [...data].sort(sortByType);

        const showASIN_SKU = ["productGrowthId", "salesByState", "topSellingProducts", "topSellingCategory"].includes(tableId);

        let headers = Object.keys(processedData[0] || {}).filter((h) => {
          const excludeFields = ["type", "image", "net_sales_cents", "fourth_comparison", "second_comparison", "third_comparison"];
          if (!showASIN_SKU) {
            excludeFields.push("asin", "sku", "brand");
          }
          return !excludeFields.includes(h);
        });
        headers = headers.filter(
          h => h !== "actual_units_display"
        );

        headersRef.current = headers;

        // Apply sequence.columns if defined and is a valid array
        if (Array.isArray(sequence?.columns) && sequence.columns.length > 0) {
          headers = sequence.columns.filter((col) => headers.includes(col));
        }

        const headerLabelMap = {
          actual_units_display: "ACTUAL UNIT",
          actual_units: "ACTUAL UNIT",
          net_sales: "NET SALES",
          wow: "WoW%",
          mom: "MoM%",
        };
        const tableHTML = `

        <div class="table-scroll-wrapper" id="scroll-wrapper-${tableId}">
          <table id="${tableId}" class="display table freeze-table" style="width:100%">
            <thead class="table-header">
              <tr>
                ${enableSelection ? `<th class="sticky-col checkbox-col not_export table-header-content"><input type="checkbox" id="select-all-on-page" /></th>` : ""}
                ${headers
            .filter(h => h !== idKey && h !== "actual_units_display")
            .filter(key => visibleColumns.length === 0 || visibleColumns.includes(key))
            .map((h, idx) => {
              let label = headerLabelMap[h]
                ? headerLabelMap[h]
                : h
                  .replace(/_/g, " ")
                  .split(" ")
                  .map(word => {
                    const lower = word.toLowerCase();
                    if (lower === "percentage") return "%";
                    if (lower === "ads") return "Ads";
                    if (lower === "tacos") return "TACoS";
                    if (lower === "wow") return "WoW%";
                    if (lower === "mom") return "MoM%";
                    return word.toUpperCase();
                  })
                  .join(" ");
              const extraClass = (!enableSelection && idx === 0) || (enableSelection && idx === 0)
                ? "sticky-col name-col table-header"
                : "table-header";
              return `<th class="${extraClass}">${label}</th>`;
            })
            .join("")}
              </tr>
            </thead>
            <tbody style="vertical-align: middle;"> 
              ${processedData
            .map(row => {
              const rowId = row[idKey]?.toString() || "";
              const rowType = row[typeKey]?.toString() || "";
              const isChecked = selectedIdsRef.current.has(rowId) ? "checked" : "";

              return `
                    <tr data-id="${rowId}" data-type="${rowType}">
                      ${enableSelection
                  ? `<td class="sticky-col checkbox-col not_export table-header-content"><input type="checkbox" class="row-checkbox normal-custom-checkbox" data-id="${rowId}" data-type="${rowType}" ${isChecked} /></td>`
                  : ""
                }
                      ${headers
                  .filter(key => key !== idKey && key !== "actual_units_display")
                  .filter(key => visibleColumns.length === 0 || visibleColumns.includes(key))
                  .map((key, idx) => {
                    const cellValue = key === "actual_units"?row["actual_units_display"]:row[key];
                    const orderValue = parseCurrency(cellValue);
                    const extraClass = (!enableSelection && idx === 0) || (enableSelection && idx === 0)
                      ? "sticky-col name-col table-header-content"
                      : "";

                    const cellContent =
                      key === "type" || key === "image" || key === "net_sales_cents"
                        ? ""
                        : key === "streak"
                          ? (() => {
                            const streakCount = Number(row[key]) || 0;
                            return `
                                    <div style="display:flex; gap:6px;">
                                      ${Array.from({ length: streakCount })
                                .map(
                                  () =>
                                    `<span style="width:10px; height:10px; border-radius:50%; background:#34c759; display:inline-block;"></span>`
                                )
                                .join("")}
                                    </div>
                                  `;
                          })()
                          : key === "name"
                            ? `<div style="display: flex; align-items: center; gap: 10px;">
                               ${!row.hasOwnProperty("image")
                              ? "" :
                              row["image"] || row["type"] === "Product" || row["type"] === "variation" || row["type"] === "Parent Product"
                                ? `<img src="${row["image"] && row["image"].trim() !== "" ? row["image"] : dummyImage
                                }" alt="${row["parent_category"] || "image"}" width="70" height="70" style="border-radius:4px;object-fit:cover; " />`
                                : (row["image"]?.trim() === "" || row["image"] === null) ? `<img alt="Dummy image" src="${dummyImage}" width="70" height="70" class="dummy-img"/>` : `` //`<img width="70" height="70" class="dummy-img"/>`
                            }
                                <div style="min-width:200px; font-size:14px; line-height:1.4;">
                                  ${row?.asin?.trim?.() || row?.sku?.trim?.() || row?.brand?.trim?.()
                              ? (() => {
                                // Determine clickable value: ASIN first, then SKU
                                const isAsinClickable = row?.asin?.trim?.();
                                const isSkuClickable = !isAsinClickable && row?.sku?.trim?.();

                                return `
                                            ${isAsinClickable
                                    ? `<div style="cursor:pointer; color:#007bff;" onclick="window.open('https://www.amazon.in/dp/${row["asin"]}', '_blank')">
                                                     ASIN: ${row["asin"]}
                                                   </div>`
                                    : ""
                                  }
                                            ${isSkuClickable
                                    ? `<div style="cursor:pointer; color:#007bff;" onclick="window.open('https://www.amazon.in/s?k=${row["sku"]}', '_blank')">
                                                     SKU: ${row["sku"]}
                                                   </div>`
                                    : row?.sku?.trim?.() && isAsinClickable
                                      ? `<div>SKU: ${row["sku"]}</div>`
                                      : ""
                                  }
                                            ${row?.brand?.trim?.()
                                    ? `<div style="color:#555;">
                                                     Brand: ${row["brand"]}
                                                   </div>`
                                    : ""
                                  }
                                          `;
                              })()
                              : ""
                            }
                                </div>

                               </div>`
                            : key === "wow" || key === "mom" || key === "first_comparison" && typeof cellValue === "number"
                              ? `<b><span style="color:${cellValue < 0 ? "#ff4d4f" : "#34c759"}">
                                   ${cellValue > 0 ? `+${cellValue}` : cellValue}%
                                 </span></b>`
                              : cellValue ?? "";



                    return orderValue == null
                      ? `<td class="${extraClass}">${cellContent}</td>`
                      : `<td class="${extraClass}" data-order="${orderValue}">${cellContent}</td>`;
                  })
                  .join("")}
                    </tr>`;
            })
            .join("")}
            </tbody>
          </table>
        `;

        // containerRef.current.innerHTML = tableHTML;
        containerRef.current.innerHTML = showSelectColumn ? `
          <div class="table-toolbar-custom">
            
            <div style="display:flex; flex-direction:column; padding-left:6px">
              <div class="custom_title" style="font-weight:600; font-size:16px;">
                ${title || ""}
              </div>
              <div style="font-size:12px; color:#666;">
                ${range.range || ""}
              </div>
            </div>

            <div style="display:flex; gap:10px; align-items:center; white-space:nowrap;">
              
              <div id="column-dropdown-${tableId}"></div>

              <div id="download-btn-${tableId}"></div>
            </div>
          </div>

          ${tableHTML}
        `: tableHTML;

        setTimeout(() => {
          const container = document.getElementById(`column-dropdown-${tableId}`);
          if (!container) return;

          container.innerHTML = `
          <button id="col-btn-${tableId}" class="btn btn-secondary buttons-csv buttons-html5">
          <i class="bi bi-funnel"></i>
          </button>

          <div class="col-dropdown-box" id="col-dropdown-box-${tableId}"></div>
        `;

          const btn = document.getElementById(`col-btn-${tableId}`);
          const box = document.getElementById(`col-dropdown-box-${tableId}`);
          const excludedColumns = ["id", "asin", "image", "name", "sku", "brand", "status", "net_sales_cents", "actual_units_display", "fourth_comparison", "second_comparison", "third_comparison"];
          btn.onclick = () => {
            box.style.display = box.style.display === "none" ? "block" : "none";
            renderCheckboxList();
          };
          const filteredColumns = allColumns.filter(colObj => {
            if (colObj.key === "wow") return hasWow;
            if (colObj.key === "mom") return hasMom;
            return true;
          });

          const renderCheckboxList = () => {
            box.innerHTML = `
          <div class="dropdown-select-all">
            <input type="checkbox" id="select-all-cols-${tableId}" />
            <label style="font-weight:500;">Select All</label>
          </div>
            <div style="max-height:180px; overflow:auto;">
              ${filteredColumns
                .filter(colObj => !excludedColumns.includes(colObj.key))
                .map(colObj => {
                  const col = colObj.key;

                  const label = col
                    .replace(/_/g, " ")
                    .split(" ")
                    .map(word => {
                      const lower = word.toLowerCase();
                      if (lower === "percentage") return "%";
                      if (lower === "ads") return "Ads";
                      if (lower === "tacos") return "TACoS";
                      if (lower === "ctr") return "CTR";
                      if (lower === "cpc") return "CPC";
                      if (lower === "wow") return "WoW";
                      if (lower === "mom") return "MoM";
                      return word.charAt(0).toUpperCase() + word.slice(1);
                    })
                    .join(" ");

                  return `
                    <div class="truncate-2-lines product-label">
                      <input type="checkbox" 
                        ${tempColumns.includes(col) ? "checked" : ""} 
                        data-col="${col}" />
                      <label>${label}</label>
                    </div>
                  `;
                }).join("")}
            </div>

            <div style="display:flex; justify-content:space-between; margin-top:10px;">
            <button id="close-col-${tableId}" class="btn btn-light w-sm-auto px-3 py-0 rounded-3 border shadow-sm">Close</button>
            <button id="update-col-${tableId}" class="btn plan-detail-card-btn rounded-3">Update</button>
            </div>
          `;


            const selectAllEl = document.getElementById(`select-all-cols-${tableId}`);
            const columnCheckboxes = box.querySelectorAll("input[data-col]");

            // all valid columns
            // const allColumns = headersRef.current.filter(col => col !== "id");
            const dropdownColumns = filteredColumns
              .filter(colObj => !excludedColumns.includes(colObj.key))
              .map(c => c.key);

            // ✅ set initial state
            selectAllEl.checked = dropdownColumns.every(col => tempColumns.includes(col));
            selectAllEl.onchange = (e) => {
              if (e.target.checked) {
                setTempColumns([...new Set([...tempColumns, ...dropdownColumns])]);

                columnCheckboxes.forEach(cb => {
                  cb.checked = true;
                });

              } else {
                const updated = tempColumns.filter(col => !dropdownColumns.includes(col));
                setTempColumns(updated);

                columnCheckboxes.forEach(cb => {
                  cb.checked = false;
                });
              }
            };

            columnCheckboxes.forEach(cb => {
              cb.onchange = (e) => {
                const col = e.target.getAttribute("data-col");

                let updated;

                if (e.target.checked) {
                  updated = [...tempColumns, col];
                } else {
                  updated = tempColumns.filter(c => c !== col);
                }

                setTempColumns(updated);

                selectAllEl.checked = dropdownColumns.every(col => updated.includes(col));
              };
            });
            document.getElementById(`update-col-${tableId}`).onclick = async () => {
              const selectedCols = [];

              box.querySelectorAll("input[data-col]").forEach(cb => {
                if (cb.checked) selectedCols.push(cb.getAttribute("data-col"));
              });

              const excludedColumns = ["id", "asin", "image", "name", "sku", "brand", "status", "net_sales_cents","actual_units_display"];

              // const finalSelectedCols = [
              //   ...new Set([...selectedCols, ...excludedColumns])
              // ];
              const finalSelectedCols = [
                ...new Set([...selectedCols, ...excludedColumns])
              ].filter(col =>  col !== "actual_units_display");

              setVisibleColumns(finalSelectedCols);
              setTempColumns(finalSelectedCols);

              onColumnChange?.(finalSelectedCols, headersRef.current);

              box.style.display = "none";
            };
            document.getElementById(`close-col-${tableId}`).onclick = () => {
              setTempColumns(visibleColumns);
              box.style.display = "none";
            };
          };
        }, 0);

        const idColIndex = headers.findIndex(h => h === idKey);
        const checkboxOffset = enableSelection ? 1 : 0;

        window.productGrowthDataTable({
          selector: `#${tableId}`,
          custom_class: "normalTable",
          pageLength: defaultPageLength,
          ordering: true,
          order: [],
          // title: title,
          // range: range,
          columnDefs: [
            ...(enableSelection ? [{ targets: 0, orderable: false }] : []),
            { targets: idColIndex + checkboxOffset, visible: false },
          ],
          downloadBtnPermission
        });

        const $table = $(`#${tableId}`).DataTable();
        $table.draw();


      } catch (err) {
        console.error("Error initializing DataTable:", err);
      }
    };

    initialize();
    setTimeout(() => {
      const buttons = document.querySelector(`#${tableId}_wrapper .dt-buttons`);
      const downloadBtn = document.getElementById(`download-btn-${tableId}`);

      if (buttons && downloadBtn) {
        downloadBtn.appendChild(buttons);
      }

      // if (wrapper && downloadBtn) {
      //   downloadBtn.appendChild(wrapper); // move download inside your toolbar
      // }
    }, 0);

    // const wrapper = document.getElementById(`${tableId}_wrapper`);
    // if (wrapper) {
    //   const topRow = wrapper.querySelector('.row');
    //   if (topRow) topRow.style.display = "none";
    // }

    return () => {
      const existingTable = $(`#${tableId}`);
      if ($.fn.DataTable.isDataTable(existingTable)) {
        existingTable.DataTable().clear().destroy(true);
        existingTable.remove();
      }
      $(`#${tableId}`).off();
    };
  }, [data, tableId, idKey, enableSelection, defaultPageLength, refreshTrigger, loading]);

  const getNoDataHTML = () => {
    return `

      <div class="no-data-wrapper" style="height: 100%; display: flex; flex-direction: column;">
        
        <!-- Heading (Top Left) -->
        ${title ? `
          <div class="kpi-sub-heading" style="text-align: left; font-weight: 600; padding: 12px 16px;">
            ${title}
          </div>
        ` : ""}

        <!-- Center Message -->
        <div class="no-data-found" style="
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          No data found for the given criteria.
        </div>
      </div>
    `;
  };

  const getSkeletonTableHTML = () => {
    const dynamicLevel = filterData?.level || "default";
    const sequence = {
      top10: ["NAME", "ACTUAL UNITS", "WoW%", "NET SALES"],
      low10: ["NAME", "ACTUAL UNITS", "WoW%", "NET SALES"],
      fastestGrowing: ["NAME", "ACTUAL UNITS", "WoW%", "NET SALES"],
      declining: ["NAME", "ACTUAL UNITS", "WoW%", "NET SALES"],
      heroes: ["NAME", "NET SALES", "ACTUAL UNITS", "Wow%", "STREAK"],
    };

    // Pick headers based on tableId, or fallback to a default set
    const headers = sequence[tableId] || ["NAME", "ACTUAL UNITS", "WoW%", "NET SALES"];

    const skeletonRow = `
      <tr>
        ${enableSelection ? "<td><div class='skeleton-box skeleton-checkbox'></div></td>" : ""}
        ${headers.map((_, idx) => {
      if (idx === 0) {
        return `
              <td>
                <div class="skeleton-product">
                  <div class="skeleton-image"></div>
                  <div class="skeleton-text-group">
                    <div class="skeleton-title"></div>
                    <div class="skeleton-subtitle"></div>
                  </div>
                </div>
              </td>`;
      }
      return "<td><div class='skeleton-box'></div></td>";
    }).join("")}
      </tr>`;

    return `
    <div class="table-wrapper">
      <div class="table-toolbar">
        <div class="search-placeholder">
          <div class="skeleton-search-icon"></div>
          <div class="skeleton-search-input"></div>
        </div>
        <div class="export-icons">
          <div class="skeleton-icon"></div>
          <div class="skeleton-icon"></div>
        </div>
      </div>
      <table id="${tableId}" class="display table" style="width:100%">
        <thead class="table-header">
          <tr>
            ${enableSelection ? "<th class='skeleton-th'></th>" : ""}
            ${headers.map(h => `<th class='skeleton-th'>${h}</th>`).join("")}
          </tr>
        </thead>
        <tbody>
          ${Array.from({ length: 10 }).map(() => skeletonRow).join("")}
        </tbody>
      </table>
    </div>
    `;
  };

  const showNoDataMessage = delayedEmpty;

  return (
    <>
      <div ref={containerRef} />
    </>
  );
};

export default NormalTable;
